/**
 * Feuille de brouillon — le panneau.
 *
 * S'ouvre par-dessus l'exercice (l'énoncé, les questions et le QCM ne bougent
 * pas). Mobile : panneau du bas, presque plein écran. Dès la tablette (≥ 768 px),
 * le panneau s'élargit (≈ 92 % de l'écran, plafonné à 1 120 px) ; dès 1 024 px,
 * le papier à gauche, l'énoncé et la palette à droite (décision du 09/10/2026).
 *
 * Aucune IA : chaque bout posé est jugé sur l'appareil par @jeraisonne/reasoner.
 */

import './scratch-sheet.css';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, CircleHelp, Lightbulb, Shapes, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import type { ScratchSession, SessionState } from './scratch-session';
import { SheetPaper } from './sheet-paper';
import { SheetPalette } from './sheet-palette';
import { FREE_SHEET_NOTE } from './free-sheet';
import { SheetTutorial, hasSeenScratchTutorial, markScratchTutorialSeen } from './sheet-tutorial';

export function useSessionState(session: ScratchSession): SessionState {
  return useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** L'énoncé de la question, replié sur mobile (on le déplie d'une touche). */
function StatementPanel({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 4);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <section
      aria-label="Énoncé de la question"
      className="sc-statement relative rounded-2xl px-3 pb-1 pt-2 lg:flex lg:max-h-[42vh] lg:flex-col lg:px-4 lg:pb-3 lg:pt-3"
    >
      <p className="sc-fam-label mb-1 hidden lg:block">Énoncé</p>
      <div
        ref={boxRef}
        className={cn(
          'text-[15px] leading-relaxed',
          open
            ? 'max-h-[38dvh] overflow-y-auto overscroll-contain'
            : 'max-h-[4.75rem] overflow-hidden md:max-h-[6.5rem]',
          'lg:max-h-none lg:min-h-0 lg:flex-1 lg:overflow-y-auto',
        )}
      >
        {children}
      </div>
      {!open && overflows && (
        <span
          className="sc-statement-fade pointer-events-none absolute inset-x-3 bottom-9 h-6 lg:hidden"
          aria-hidden
        />
      )}
      {(overflows || open) && (
        <button
          type="button"
          className="font-display -mb-1 -ml-1 min-h-11 px-1 text-sm font-semibold text-[var(--sc-primary-ink)] lg:hidden"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Réduire l’énoncé' : 'Voir tout l’énoncé'}
        </button>
      )}
    </section>
  );
}

export interface ScratchSheetProps {
  session: ScratchSession;
  /** l'énoncé de la question, tel que le player l'affiche */
  statement: ReactNode;
  onClose: () => void;
  /** aperçu tuteur d'une feuille pas encore publiée */
  draft?: boolean;
  /** ouvre la Boîte à outils par-dessus la feuille */
  onToolbox?: () => void;
  /**
   * prise en main à l'ouverture : `auto` (défaut) = la 1re fois sur l'appareil,
   * `always` = toujours (entraînement avant un défi live), `never` = jamais
   * toute seule (pendant un live : pas de temps perdu ; le « ? » reste là)
   */
  tutorialOnOpen?: 'auto' | 'always' | 'never';
}

export function ScratchSheet({
  session,
  statement,
  onClose,
  draft,
  onToolbox,
  tutorialOnOpen = 'auto',
}: ScratchSheetProps) {
  const state = useSessionState(session);
  const guard = useRef(false);
  const sheetRef = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // prise en main : toute seule à la 1re ouverture (feuille encore modifiable)
  const [tutorial, setTutorial] = useState(
    () =>
      tutorialOnOpen === 'always' ||
      (tutorialOnOpen === 'auto' && !hasSeenScratchTutorial() && !session.getSnapshot().locked),
  );
  useEffect(() => {
    if (tutorial) markScratchTutorialSeen();
  }, [tutorial]);

  // page derrière bloquée, Échap, focus gardé dans la feuille
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const back = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const s = session.getSnapshot();
        if (s.insertAt !== null || s.cursor) session.finishEditing();
        else onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const items = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (
        e.shiftKey &&
        (document.activeElement === first || document.activeElement === sheetRef.current)
      ) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
      back?.focus?.();
    };
  }, [session]);

  // toucher ailleurs termine l'insertion et/ou sort de la fraction ; le geste
  // touché s'exécute ensuite normalement (comme dans le prototype)
  const onClickCapture = (e: ReactMouseEvent) => {
    if (guard.current) return;
    const s = session.getSnapshot();
    if (s.insertAt === null && !s.cursor) return;
    const t = e.target as HTMLElement;
    if (t.closest?.('[data-sc-keep="all"], [data-k]')) return;
    if (s.cursor && t.closest?.('[data-sc-keep="frac"]')) return;
    session.finishEditing();
  };

  return createPortal(
    <div
      className="sc-root fixed inset-0 z-[70] flex items-end justify-center md:items-center md:p-4"
      onPointerDownCapture={() => {
        guard.current = false;
      }}
      onClickCapture={onClickCapture}
    >
      <div className="sc-scrim absolute inset-0" onClick={onClose} aria-hidden />
      <section
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="scratch-sheet-title"
        tabIndex={-1}
        data-testid="scratch-sheet"
        className="sc-sheet native:h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] relative flex h-[calc(100dvh-0.5rem)] w-full flex-col overflow-hidden rounded-t-3xl outline-none md:h-[min(92dvh,960px)] md:w-[92vw] md:max-w-[1120px] md:rounded-3xl"
      >
        <header className="relative flex flex-none items-center gap-1 pb-1 pl-4 pr-2 pt-3 md:pl-5 md:pt-2">
          <span
            className="sc-grabber absolute left-1/2 top-[7px] h-1 w-10 -translate-x-1/2 rounded-full md:hidden"
            aria-hidden
          />
          <h2
            id="scratch-sheet-title"
            className="font-display min-w-0 flex-1 truncate text-[17px] font-bold md:text-lg"
          >
            Feuille de brouillon
          </h2>
          {draft && (
            <span className="font-display mr-1 hidden rounded-full border border-dashed border-[var(--sc-line-2)] px-2.5 py-1 text-xs font-semibold text-[var(--sc-muted)] sm:inline">
              Aperçu · non publiée
            </span>
          )}
          {!session.free && (
            <>
              <span
                className="sc-hora"
                aria-label={`${state.hora} HORA gagnés sur cette feuille`}
                data-testid="scratch-hora"
              >
                +{state.hora} HORA
              </span>
              <button
                type="button"
                className="sc-icon-btn"
                data-hint=""
                data-tuto="hint"
                aria-label="Coup de pouce"
                disabled={state.locked}
                onClick={() => session.hint()}
              >
                <Lightbulb className="h-5 w-5" aria-hidden />
              </button>
            </>
          )}
          {onToolbox && (
            <button
              type="button"
              className="sc-icon-btn"
              aria-label="Boîte à outils"
              title="Boîte à outils"
              data-testid="scratch-toolbox-open"
              onClick={onToolbox}
            >
              <Shapes className="h-5 w-5" aria-hidden />
            </button>
          )}
          <button
            type="button"
            className="sc-icon-btn sc-icon-btn-quiet"
            aria-label="Comment marche la feuille ?"
            data-testid="scratch-tutorial-open"
            onClick={() => setTutorial(true)}
          >
            <CircleHelp className="h-[18px] w-[18px]" aria-hidden />
          </button>
          <button
            type="button"
            className="sc-icon-btn"
            aria-label="Revenir à la question"
            onClick={onClose}
          >
            <ChevronDown className="h-6 w-6 md:hidden" aria-hidden />
            <X className="hidden h-5 w-5 md:block" aria-hidden />
          </button>
        </header>

        {session.free && (
          <p
            className="mx-3 mb-1 flex-none rounded-xl bg-[var(--sc-gold-soft)] px-3 py-2 text-sm text-[var(--sc-ink)] lg:mx-4"
            data-testid="scratch-free-note"
          >
            {FREE_SHEET_NOTE}
          </p>
        )}
        <div className="sc-body min-h-0 flex-1 gap-2 px-3 lg:gap-4 lg:px-4 lg:pb-4">
          <div className="sc-area-statement min-h-0 lg:flex lg:flex-col">
            <StatementPanel>{statement}</StatementPanel>
          </div>
          <div className="sc-area-paper min-h-0">
            <SheetPaper state={state} session={session} guard={guard} />
          </div>
          <div className="sc-area-palette -mx-3 min-h-0 lg:mx-0 lg:flex lg:flex-col lg:rounded-2xl lg:border lg:border-[var(--sc-line)] lg:bg-[var(--sc-surface)]">
            <SheetPalette state={state} session={session} onClose={onClose} />
          </div>
        </div>
        {tutorial && (
          <SheetTutorial
            container={sheetRef}
            free={session.free}
            onDone={() => setTutorial(false)}
          />
        )}
      </section>
    </div>,
    document.body,
  );
}
